/**
 * Decision maths for a closed month. Pure functions over published records, so
 * every figure can be traced to transactions. NGN only; USD is reported apart.
 * Unknown stays unknown: a month with no recorded income has no surplus.
 */
import type { Account, CategoryMonth, Month, Transaction } from '../data/types';
import { isInvestmentAccount, balanceAsOf } from '../data/types';
import { suggestPayeeKey } from './payee';

/** Reserve goal in months of personal spend (owner, 10 Oct 2026: 3 now, 6 later). */
export const RESERVE_TARGET_MONTHS = 3;
export const RESERVE_LATER_MONTHS = 6;

export interface MonthMoney {
  income: number;
  otherInflows: number;
  fixed: number;
  lifestyle: number;
  otherSpend: number;
  personal: number;
  invested: number;
  business: number;
  /** Migrate World costs paid outside the tracked accounts (logged in the app), included in business. */
  businessOutside: number;
  /** income − personal − business. Null when no income is recorded. */
  available: number | null;
  /** available − invested: positive = unallocated surplus, negative = drawn from savings. */
  leftover: number | null;
}

const ngn = (t: Transaction) => t.currency === 'NGN';
const sum = (xs: Transaction[]) => xs.reduce((s, t) => s + t.amount, 0);
const internal = (t: Transaction) => Boolean(t.is_internal_transfer) || t.category_type === 'transfer';
const business = (t: Transaction) => Boolean(t.is_business_funding) || t.category_type === 'business';

/** Same exclusions as recompute_month in the database, so totals agree with the published month. */
export function isPersonalSpend(t: Transaction): boolean {
  return t.direction === 'debit' && ngn(t) && !internal(t) && !business(t)
    && !['investment', 'transfer', 'business', 'income', 'property'].includes(t.category_type ?? 'other');
}

/**
 * @param outsideBusiness Migrate World costs for the month paid outside the tracked accounts
 *   (cash, Wise, someone paying for you). Counted with business so every screen shows one figure.
 */
export function monthMoney(txs: Transaction[], outsideBusiness = 0): MonthMoney {
  const debits = txs.filter((t) => t.direction === 'debit' && ngn(t));
  const credits = txs.filter((t) => t.direction === 'credit' && ngn(t) && !internal(t));
  const personalTx = debits.filter(isPersonalSpend);
  const income = sum(credits.filter((t) => t.category_type === 'income'));
  const fixed = sum(personalTx.filter((t) => t.category_type === 'fixed'));
  const lifestyle = sum(personalTx.filter((t) => t.category_type === 'lifestyle'));
  const personal = sum(personalTx);
  const invested = sum(debits.filter((t) => t.category_type === 'investment'));
  const biz = sum(debits.filter((t) => business(t) && !internal(t))) + outsideBusiness;
  const available = income > 0 ? income - personal - biz : null;
  return {
    income,
    otherInflows: sum(credits.filter((t) => t.category_type !== 'income')),
    fixed, lifestyle, otherSpend: personal - fixed - lifestyle, personal,
    invested, business: biz, businessOutside: outsideBusiness,
    available,
    leftover: available === null ? null : available - invested,
  };
}

export interface Reserve {
  /** NGN bank accounts plus money-market accounts: money you can reach within days. */
  liquid: number | null;
  /** Oldest balance date among the counted accounts. */
  asOf: string | null;
  newestAsOf: string | null;
  /** Accounts whose balance is older than the newest one: named so a stale figure is never hidden. */
  stale: { name: string; asOf: string }[];
  accounts: number;
  avgMonthlySpend: number | null;
  monthsCovered: number | null;
  target: number | null;
  gap: number | null;
}

export function isLiquidNgn(a: Account): boolean {
  return a.currency === 'NGN' && a.current_balance !== null
    && (!isInvestmentAccount(a) || (a.account_type ?? '').toLowerCase() === 'money_market');
}

/**
 * What a sealed month did, in one honest sentence. Overspend (spending + Migrate World above income)
 * is kept apart from investing: money moved into investments is still the owner's, so it is never
 * described as lost.
 */
export type MonthVerdict =
  | { kind: 'unknown' }
  | { kind: 'left'; amount: number }
  | { kind: 'investedBeyond'; spare: number; invested: number; fromSavings: number }
  | { kind: 'overspent'; amount: number; invested: number };

export function monthVerdict(m: MonthMoney): MonthVerdict {
  if (m.available === null || m.leftover === null) return { kind: 'unknown' };
  if (m.leftover >= 0) return { kind: 'left', amount: m.leftover };
  if (m.available >= 0) return { kind: 'investedBeyond', spare: m.available, invested: m.invested, fromSavings: -m.leftover };
  return { kind: 'overspent', amount: -m.available, invested: m.invested };
}

export function reserve(accounts: Account[], published: Month[], targetMonths = RESERVE_TARGET_MONTHS): Reserve {
  const liquidAccts = accounts.filter(isLiquidNgn);
  const liquid = liquidAccts.length ? liquidAccts.reduce((s, a) => s + Number(a.current_balance), 0) : null;
  const recent = published.filter((m) => m.personal_spend_actual !== null).slice(0, 3);
  const avg = recent.length ? recent.reduce((s, m) => s + Number(m.personal_spend_actual), 0) / recent.length : null;
  const target = avg === null ? null : avg * targetMonths;
  const dated = liquidAccts.map((a) => ({ name: a.name, asOf: balanceAsOf(a) })).sort((x, y) => x.asOf.localeCompare(y.asOf));
  const oldest = dated[0] ?? null;
  const newest = dated[dated.length - 1] ?? null;
  return {
    liquid,
    asOf: oldest?.asOf ?? null,
    newestAsOf: newest?.asOf ?? null,
    stale: oldest && newest && oldest.asOf < newest.asOf ? dated.filter((d) => d.asOf < newest.asOf) : [],
    accounts: liquidAccts.length,
    avgMonthlySpend: avg,
    monthsCovered: liquid !== null && avg ? liquid / avg : null,
    target,
    gap: liquid !== null && target !== null ? Math.max(0, target - liquid) : null,
  };
}

/** Months to close a gap at a monthly pace; null when it cannot be closed at that pace. */
export function monthsToClose(gap: number | null, perMonth: number): number | null {
  if (gap === null || perMonth <= 0) return null;
  return gap === 0 ? 0 : Math.ceil(gap / perMonth);
}

/** A cut is worth naming only if it frees at least this much a month (owner-facing materiality). */
export const MATERIAL_CUT = 50_000;
export const isMaterialCut = (c: CategoryCut) => c.overCap >= MATERIAL_CUT || c.aboveAvg >= MATERIAL_CUT;

export interface CategoryCut {
  name: string;
  actual: number;
  cap: number | null;
  overCap: number;
  avg: number | null;
  aboveAvg: number;
}

/** Lifestyle categories ranked by how far they ran over their ceiling or their own recent average. */
export function categoryCuts(current: CategoryMonth[], priors: CategoryMonth[][]): CategoryCut[] {
  return current
    .filter((c) => c.category_type === 'lifestyle' && Number(c.actual_amount ?? 0) > 0)
    .map((c) => {
      const actual = Number(c.actual_amount ?? 0);
      const cap = c.budget_amount === null ? null : Number(c.budget_amount);
      const history = priors.map((p) => p.find((x) => x.category_id === c.category_id)).filter(Boolean)
        .map((x) => Number(x!.actual_amount ?? 0));
      const avg = history.length ? history.reduce((s, v) => s + v, 0) / history.length : null;
      return {
        name: c.name, actual, cap,
        overCap: cap !== null && cap > 0 ? Math.max(0, actual - cap) : 0,
        avg, aboveAvg: avg !== null ? Math.max(0, actual - avg) : 0,
      };
    })
    .sort((a, b) => (b.overCap + b.aboveAvg) - (a.overCap + a.aboveAvg) || b.actual - a.actual);
}

export interface Payee {
  key: string;
  category: string | null;
  count: number;
  total: number;
}

/** Where discretionary spend went, by payee. Fixed obligations (rent, child support) are left out. */
export function topPayees(txs: Transaction[], limit = 8): Payee[] {
  const by = new Map<string, Payee>();
  for (const t of txs.filter((x) => isPersonalSpend(x) && x.category_type !== 'fixed')) {
    const key = suggestPayeeKey(t.description) || t.description.toUpperCase().replace(/[^A-Z ]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 32);
    const p = by.get(key) ?? { key, category: t.category_name, count: 0, total: 0 };
    p.count += 1;
    p.total += t.amount;
    by.set(key, p);
  }
  return [...by.values()].sort((a, b) => b.total - a.total).slice(0, limit);
}
